import React, { Component } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { GoRepo, GoLock } from "react-icons/go";
import { Link } from "react-router-dom";
import Select from 'components/controls/select';
import Title from 'context/title';
import Navigation from 'context/nav';
import { enabledTypes } from 'context/limitsCatalog';

/**
 * A type option shows its human label AND its dotted string.
 *
 * The label alone no longer identifies a type: the dotted string IS the identity now — it is what
 * the CLI, a manifest and the REST body all name, and it is what the blob will show on every
 * surface afterwards. Someone picking "Workflow" here should see the thing they will be typing
 * later.
 *
 * Deliberately NOT folded into the option's `label`: react-select searches and creates options from
 * that string, so it stays the human name and typing "workflow" keeps matching.
 *
 * Module-level rather than a class property so it can be tested as what it is — a pure function of
 * an option — without standing up the page and its API.
 */
export function formatTypeOption(option) {
  return (
    <span className="bh-type-option">
      {option.label}
      <code className="bh-type-option-id">{option.value}</code>
    </span>
  );
}

export default class Page extends Component {
  constructor(props) {
    super(props);

    this.typeOptions = null;

    this.state = {
      org: null,
      notFound: false,
      blob: {
        alias: "",
        visibility: "private",
        type: null
      },
      data: {
        type: null
      },
      progress: false,
      error: null
    };

    this.loadData();
  }

  componentDidMount() {
    Title.set("New Blob");
  }

  loadData() {
    const orgId = this.props.match.params.orgId;

    // Query org profile
    this.props.app.api.orgsIdGet(orgId)
      .then(response => {
        this.processOrg(response.data.org);
      }, error => {
        this.setState({ notFound: true });
      });

    this.props.app.api.orgsIdLimitsGet(orgId)
      .then(response => {
        this.processTypes(enabledTypes(response.data && response.data.groups));
      }, error => {
        this.setState({ notFound: true });
      });
  }

  processOrg(org) {
    // Prevent blob creation for alien orgs
    if ( !this.props.app.api.canAdministerOrg(org) ) {
      this.setState({ notFound: true });
      return;
    }

    // Remember org profile
    this.setState({ org: org });
  }

  processTypes(typeOptions) {
    this.typeOptions = typeOptions;
    const first = typeOptions.length ? typeOptions[0] : null;
    this.setState({
      data: { type: first },
      blob: { ...this.state.blob, type: first ? first.value : null }
    });
  }

  handleCreateSubmit = (event) => {
    event.preventDefault();

    // Check form validity
    const form = event.currentTarget;
    if ( form.checkValidity() === false ) {
      return;
    }

    this.setState({
      progress: true,
      error: null
    });

    // Create blob
    this.props.app.api.blobsPost(this.state.org.id, this.state.blob)
      .then(response => {
        this.proceedToCreatedBlob();
      }, error => {
        this.setState({
          progress: false,
          error: error.response.data.message
        });
      });
  }

  proceedToCreatedBlob() {
    Navigation.push(this.props.history, Navigation.blobPath(this.state.org, this.state.blob));
  }

  render() {
    if ( this.state.notFound ) {
      // Redirect to self profile if org is not found
      Navigation.push(this.props.history, Navigation.selfUserProfilePath());
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="primary">Redirecting to self profile page...</Alert>
          </Container>
        </>
      );
    }

    if ( !this.state.org || !this.typeOptions ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="primary">Fetching relevant details...</Alert>
          </Container>
        </>
      );
    }

    if ( 0 === this.typeOptions.length ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="warning">
              No blob type is enabled for this organization.{" "}
              <Link to={Navigation.orgSettingsLimitsPath(this.props.match.params.orgId)}>Its limits</Link>
              {" "}show each type and whether it is enabled.
            </Alert>
          </Container>
        </>
      );
    }

    const orgId = this.props.match.params.orgId;
    return (
      <>
        <Container>
          <Row>
            <Col sm={2}></Col>
            <Col sm={8}>
              <Container className="blob-header-row header-message-row">
                <h5>
                  Create a New Blob
                </h5>
                <div>
                  Blob contains structured data that can be iterated on by contributors, versioned, and consumed.
                </div>
                <hr/>
              </Container>
              <Container className="blob-header-row">
                <Form onSubmit={this.handleCreateSubmit}>
                  <Form.Group>
                    <Form.Label>
                      <strong>Alias</strong> <span className="text-muted">(required)</span>
                    </Form.Label>
                    <Row>
                      <Col sm={3}>
                        <Form.Control
                          required
                          type="text"
                          size="sm"
                          autoFocus
                          value={this.state.blob.alias}
                          onChange={(event) => {
                            let blob = this.state.blob;
                            blob.alias = event.target.value;
                            this.setState({blob: blob});
                          }}
                        />
                      </Col>
                    </Row>
                    <Form.Text muted>
                      Blob aliases should be concise and meaningful.
                    </Form.Text>
                    <Form.Text muted>
                      Alias must confirm to the following requirements:
                      <ul>
                        <li>minimum of 6 characters;</li>
                        <li>maximum of 42 characters;</li>
                        <li>only alphanumeric characters along with <strong>-</strong>,<strong>_</strong> are allowed;</li>
                        <li>must be unique within the organization.</li>
                      </ul>
                    </Form.Text>
                  </Form.Group>
                  <hr/>

                  <Form.Group>
                    <Form.Label>
                      <strong>Visibility</strong>
                    </Form.Label>
                    <Form.Check
                      type="radio"
                      name="visibility"
                      id="visibility-public"
                      label={<><GoRepo/> Public</>}
                      checked={"public" == this.state.blob.visibility}
                      onChange={(event) => {
                        let blob = this.state.blob;
                        blob.visibility = event.target.checked ? "public" : "private";
                        this.setState({blob: blob});
                      }}
                    />
                    <Form.Text muted>
                      <string>Public</string> blobs are visible to the world but only chosen users can contribute to it.
                    </Form.Text>
                    <Form.Check
                      type="radio"
                      name="visibility"
                      id="visibility-private"
                      label={<><GoLock/> Private</>}
                      checked={"private" == this.state.blob.visibility}
                      onChange={(event) => {
                        let blob = this.state.blob;
                        blob.visibility = event.target.checked ? "private" : "public";
                        this.setState({blob: blob});
                      }}
                    />
                    <Form.Text muted>
                      <string>Private</string> blobs are invisible by default with both producers and consumers being added manually.
                    </Form.Text>
                  </Form.Group>
                  <hr/>

                  <Form.Group>
                    <Form.Label>
                      <strong>Type</strong>
                    </Form.Label>
                    {/* Wider than the alias field beside it, and deliberately so: this control now
                        carries a label AND a dotted type, and the longest pair
                        ("Scheduler  blobhub.compute.scheduler") needs the room. At sm=4 it wrapped
                        mid-token — "blobhub.compute.wo / rkflow" — and truncated the selected
                        value. */}
                    <Row>
                      <Col sm={7}>
                        <Select
                          options={this.typeOptions}
                          formatOptionLabel={formatTypeOption}
                          value={this.state.data.type}
                          onChange={(option) => {
                            let blob = this.state.blob;
                            blob.type = option.value;
                            this.setState({blob: blob});

                            let data = this.state.data;
                            data.type = option;
                            this.setState({data: data});
                          }}
                        />
                      </Col>
                    </Row>
                    <Form.Text muted>
                      <strong>Type</strong> determines the structure of the blob's data and operations.
                    </Form.Text>
                  </Form.Group>
                  <hr/>

                  {this.state.progress &&
                    <>
                      <Alert variant="primary">
                        Creating blob...
                      </Alert>
                    </>
                  }

                  {this.state.error &&
                    <>
                      <Alert variant="warning">
                        {this.state.error}
                      </Alert>
                      <hr/>
                    </>
                  }

                  {!this.state.progress &&
                    <>
                      <Form.Group>
                        <Button
                          type="submit"
                          variant="success"
                          size="sm">
                          Create Blob
                        </Button>
                      </Form.Group>
                    </>
                  }
                </Form>
              </Container>
            </Col>
          </Row>
        </Container>
      </>
    )
  }
}
